import { ArrowLeft } from 'lucide-react';

import { useFamilyDetail } from '../../hooks/useFamilies';

import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { useNavigate, useParams } from '@/lib/router';

const roleColors = {
  admin: 'bg-orange-100 text-orange-700',
  member: 'bg-slate-100 text-slate-600',
};

export function FamilyDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data: family, isLoading } = useFamilyDetail(id);

  if (isLoading) {
    return (
      <AppShell>
        <TopBar title="Chargement..." />
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
        </div>
      </AppShell>
    );
  }

  if (!family) return null;

  return (
    <AppShell>
      <TopBar title={family.name} />
      <div className="mx-auto max-w-4xl p-6">
        <button
          onClick={() => void navigate({ to: '/families' })}
          className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={15} /> Retour aux familles
        </button>

        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="card text-center">
            <p className="text-3xl font-bold text-slate-900">{family.members.length}</p>
            <p className="text-sm text-slate-500">Membres</p>
          </div>
          <div className="card text-center">
            <p className="text-3xl font-bold text-slate-900">{family.recipe_count}</p>
            <p className="text-sm text-slate-500">Recettes</p>
          </div>
          <div className="card text-center">
            <p className="text-sm font-medium text-slate-900">
              {new Date(family.created_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>
            <p className="text-sm text-slate-500">Date de création</p>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-4 font-semibold text-slate-900">Membres ({family.members.length})</h3>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Email</th>
                  <th className="hidden lg:table-cell">Nom</th>
                  <th className="hidden sm:table-cell">Rôle</th>
                  <th className="hidden sm:table-cell">Statut</th>
                </tr>
              </thead>
              <tbody>
                {family.members.map((m) => (
                  <tr key={m.id}>
                    <td className="font-medium text-slate-900 flex flex-col gap-2">
                      {m.user?.email ?? <span className="text-slate-500">Sans compte</span>}
                      <div className="sm:hidden flex gap-2">
                        <span className={`badge ${roleColors[m.role]}`}>{m.role}</span>
                        <span
                          className={`badge ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                        >
                          {m.status}
                        </span>
                      </div>
                    </td>
                    <td className="hidden lg:table-cell text-slate-600">
                      {m.user?.name ?? m.display_name ?? '—'}
                    </td>
                    <td className="hidden sm:table-cell">
                      <span className={`badge ${roleColors[m.role]}`}>{m.role}</span>
                    </td>
                    <td className="hidden sm:table-cell">
                      <span
                        className={`badge ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                      >
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
